import "./TierProgress.css";

const defaultLevels = [
  { name: "Core", at: 0 },
  { name: "Select", at: 25000 },
  { name: "Elite", at: 75000 },
];

const money = (n) => `$${Number(n).toLocaleString("en-US")}`;

/**
 * Props
 * - purchases  number   purchases to date (default 12650)
 * - levels     array    [{ name, at }] sorted ascending by `at`
 * - max        number   value at the far right of the track (default: last level's `at`)
 * - title      string   card heading
 * - tierName   string   override the detected current tier
 */
export default function TierProgress({
  purchases,
  levels = defaultLevels,
  max,
  title = "Your Tier Progress",
  tierName,
}) {
  const scale = max ?? levels[levels.length - 1].at;
  const pct = (v) => Math.max(0, Math.min(100, (v / scale) * 100));

  // Highest level already reached, and the next one (if any)
  const reached = [...levels].reverse().find((l) => l.at <= purchases) ?? levels[0];
  const next = levels.find((l) => l.at > purchases);
  const current = tierName ?? reached.name;

  const youPos = pct(purchases);
  const youShift = youPos < 8 ? "is-left" : youPos > 92 ? "is-right" : "";

  return (
    <section className="tp-card">
      <h2 className="tp-title">{title}</h2>

      <div className="tp-body">
        <div className="tp-top">
          <span>
            Current tier: <b>{current}</b> · Purchases to date: <b>{money(purchases)}</b>
          </span>
          {next ? (
            <strong className="tp-next">
              {money(next.at - purchases)} in purchases to {next.name} Tier
            </strong>
          ) : (
            <strong className="tp-next">Top tier reached 🎉</strong>
          )}
        </div>

        <div
          className="tp-track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={scale}
          aria-valuenow={Math.min(purchases, scale)}
          aria-label={`Tier progress: ${money(purchases)} in purchases`}
        >
          <div className="tp-fill" style={{ width: `${youPos}%` }} />

          <span className={`tp-you ${youShift}`} style={{ left: `${youPos}%` }}>
            You: {money(purchases)}
          </span>

          {levels.map((l) => (
            <span
              key={l.name}
              className={`tp-node ${l.at <= purchases ? "is-on" : ""}`}
              style={{ left: `${pct(l.at)}%` }}
            />
          ))}
        </div>

        <div className="tp-levels">
          {levels.map((l) => {
            const edge = pct(l.at) === 0 ? "is-first" : pct(l.at) === 100 ? "is-last" : "";
            return (
              <div key={l.name} className={`tp-level ${edge}`} style={{ left: `${pct(l.at)}%` }}>
                <b>{l.name}</b><br />
                <small>{money(l.at)}</small>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}